// ── Dot grid: scalar persistent illumination (Phase 1) ──
// Per-cell deterministic threshold + brightness from index hash.
// Render rule (locked invariant from docs/SPEC.md S5):
//   incoming = smoothstep(threshold, threshold + revealWidth, highestStoryTime) * brightness
//   cellLight[i] = max(cellLight[i], incoming)
//
// Cells fade UP through scalar intensity, never down. This is the brand-load-
// bearing mechanic: forward narrative leaves persistent light; backscroll
// rewinds nothing.

import { ILLUMINATION } from '../lib/illumination-params.js';
import { smoothstep } from '../lib/easing.js';

interface Cell {
  index: number;
  col: number;
  row: number;
  threshold: number;  // 0..1, deterministic from hash(index)
  brightness: number; // brightnessMin..brightnessMax, power-distributed
}

export class DotGrid {
  private cells: Cell[] = [];
  private cellLight: Float32Array = new Float32Array(0);
  private cols: number = ILLUMINATION.cols;
  private rows: number = ILLUMINATION.rows;
  private viewportW = 0;
  private viewportH = 0;

  constructor() {
    this.regenerate();
  }

  /** Deterministic 0..1 from integer index + salt (xmur3-ish). */
  private hash01(seed: number, salt: number): number {
    let h = (seed * 2654435761) ^ (salt * 1664525);
    h ^= h >>> 16;
    h = Math.imul(h, 2246822507);
    h ^= h >>> 13;
    h = Math.imul(h, 3266489909);
    h ^= h >>> 16;
    return (h >>> 0) / 0xFFFFFFFF;
  }

  private regenerate(): void {
    const total = this.cols * this.rows;
    this.cells = [];
    this.cellLight = new Float32Array(total);

    const span = ILLUMINATION.illuminationSpan;
    const tBias = ILLUMINATION.thresholdBias;
    const bMin = ILLUMINATION.brightnessMin;
    const bMax = ILLUMINATION.brightnessMax;
    const bias = ILLUMINATION.darkBias;

    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        const index = r * this.cols + c;
        const t = this.hash01(index, 0);
        const b = this.hash01(index, 1);
        const threshold = Math.pow(t, tBias) * span;
        const brightness = bMin + Math.pow(b, bias) * (bMax - bMin);
        this.cells.push({ index, col: c, row: r, threshold, brightness });
      }
    }
  }

  setViewport(w: number, h: number): void {
    this.viewportW = w;
    this.viewportH = h;

    // Responsive density. Reseed on breakpoint cross (resize accepts a flicker).
    const newCols = w < 600 ? ILLUMINATION.colsMobile : ILLUMINATION.cols;
    const newRows = w < 600 ? ILLUMINATION.rowsMobile : ILLUMINATION.rows;
    if (newCols !== this.cols || newRows !== this.rows) {
      this.cols = newCols;
      this.rows = newRows;
      this.regenerate();
    }
  }

  /**
   * Render the field for a given highestStoryTime.
   * @param highestStoryTime monotonic 0..1 (from FieldClockSystem)
   * @param alphaMultiplier global alpha cap (used by reduced-motion mode)
   */
  render(
    ctx: CanvasRenderingContext2D,
    highestStoryTime: number,
    alphaMultiplier: number = 1,
  ): void {
    const w = this.viewportW;
    const h = this.viewportH;
    if (w === 0 || h === 0) return;

    const spanW = w * ILLUMINATION.spanX;
    const spanH = h * ILLUMINATION.spanY;
    const offX = (w - spanW) * 0.5;
    const offY = (h - spanH) * 0.5;
    const stepX = this.cols > 1 ? spanW / (this.cols - 1) : 0;
    const stepY = this.rows > 1 ? spanH / (this.rows - 1) : 0;

    const revealWidth = ILLUMINATION.revealWidth;
    const { r: cR, g: cG, b: cB } = ILLUMINATION.baseColor;
    const litSize = ILLUMINATION.litCellSize;

    // Per AR (May 4 2026): only illuminating cells render. No always-painted
    // unlit substrate. Field is invisible until a cell crosses its threshold;
    // light is the only thing that exists.
    for (let i = 0; i < this.cells.length; i++) {
      const cell = this.cells[i];

      const incoming =
        smoothstep(cell.threshold, cell.threshold + revealWidth, highestStoryTime)
        * cell.brightness;

      // The locked invariant: cellLight[i] = max(prev, incoming).
      if (incoming > this.cellLight[i]) {
        this.cellLight[i] = incoming;
      }
      const lit = this.cellLight[i];

      if (lit <= 0.01) continue;

      const x = offX + cell.col * stepX;
      const y = offY + cell.row * stepY;
      const litAlpha = lit * alphaMultiplier;
      ctx.fillStyle = `rgba(${cR}, ${cG}, ${cB}, ${litAlpha})`;
      ctx.fillRect(x - litSize / 2, y - litSize / 2, litSize, litSize);
    }
  }

  /** Verify B2: how many cells exceed the visibility threshold. */
  countLit(threshold: number = 0.15): number {
    let n = 0;
    for (let i = 0; i < this.cellLight.length; i++) {
      if (this.cellLight[i] > threshold) n++;
    }
    return n;
  }

  totalCells(): number {
    return this.cellLight.length;
  }

  /**
   * Phase 2C ghost residue: on return visit, seed a fraction of the cells
   * whose threshold lies within the previous session's reach with a low
   * cellLight value. Reads as "fossil memory" of last visit.
   *
   * Selection is deterministic per cell index (third hash salt) so the
   * ghost pattern is stable, not flickery between reloads.
   */
  applyGhostResidue(highestStoryTime: number, fraction: number, alpha: number): void {
    if (highestStoryTime <= 0 || fraction <= 0 || alpha <= 0) return;
    for (const cell of this.cells) {
      if (cell.threshold > highestStoryTime) continue;
      const pick = this.hash01(cell.index, 7);
      if (pick > fraction) continue;
      // Don't overwrite cells already at higher light.
      if (this.cellLight[cell.index] < alpha) {
        this.cellLight[cell.index] = alpha;
      }
    }
  }
}
